import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileDown, Gauge, FileBadge, BarChart3, BookOpen, Layers } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [exposure, setExposure] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/admin/audit").then((res) => setLogs(res.data));
    api.get("/admin/wcppa-exposure-stats").then((res) => setExposure(res.data)).catch(() => setExposure(null));
  }, []);

  const downloadAnnexure = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/sjt-annexure", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "WCPPA_SJT_SME_Annexure.md";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/audit">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="admin-audit-heading">Audit Log</h1>
        <Button variant="outline" size="sm" onClick={downloadAnnexure} disabled={downloading} data-testid="export-sme-annexure-button">
          <FileDown className="h-4 w-4 mr-2" /> {downloading ? "Preparing…" : "Export SME Annexure (SJT)"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-10">
        <Table data-testid="admin-audit-table">
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead><TableHead>Actor</TableHead><TableHead>Role</TableHead>
              <TableHead>Action</TableHead><TableHead>Target</TableHead><TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.audit_id} data-testid={`audit-row-${l.audit_id}`}>
                <TableCell className="text-xs">{new Date(l.timestamp).toLocaleString()}</TableCell>
                <TableCell className="font-mono text-xs">{l.actor_user_id}</TableCell>
                <TableCell>{l.actor_role}</TableCell>
                <TableCell>{l.action}</TableCell>
                <TableCell className="font-mono text-xs">{l.target_type}:{l.target_id}</TableCell>
                <TableCell>{l.reason || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2" data-testid="exposure-stats-heading">
          <Gauge className="h-5 w-5 text-indigo-600" /> WCPPA Item Exposure (Calibration Readiness)
        </h2>
        {exposure && <span className="text-xs text-slate-400">{exposure.total_sessions_tracked} session(s) tracked</span>}
      </div>
      {exposure && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden max-h-[480px] overflow-y-auto" data-testid="exposure-stats-table-wrapper">
          <Table data-testid="exposure-stats-table">
            <TableHeader>
              <TableRow>
                <TableHead>Slot</TableHead><TableHead>Construct</TableHead>
                <TableHead>A</TableHead><TableHead>B</TableHead><TableHead>C</TableHead><TableHead>D</TableHead><TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exposure.slots.map((s) => (
                <TableRow key={s.slot_id} data-testid={`exposure-row-${s.slot_id}`}>
                  <TableCell className="font-mono text-xs">{s.slot_id}</TableCell>
                  <TableCell className="text-xs">{s.construct_name}</TableCell>
                  <TableCell className="text-xs">{s.counts.A}</TableCell>
                  <TableCell className="text-xs">{s.counts.B}</TableCell>
                  <TableCell className="text-xs">{s.counts.C}</TableCell>
                  <TableCell className="text-xs">{s.counts.D}</TableCell>
                  <TableCell className="text-xs font-medium">{s.total_exposures}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </DashboardShell>
  );
}
